import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, X, BellOff, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatEuro(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}

function urgencyClasses(jours) {
  if (jours <= 7) return 'border-red-500 bg-red-50 text-red-800';
  if (jours <= 15) return 'border-orange-500 bg-orange-50 text-orange-800';
  return 'border-amber-500 bg-amber-50 text-amber-800';
}

/**
 * Bannière d'alerte dashboard listant les rapports TICFE/TICGN dont au moins
 * une deadline CERFA (31/12 année+2) tombe dans les 30 prochains jours.
 *
 * Chaque ligne propose 2 actions :
 *  - "Retirer l'alerte" (dismiss) : snooze visuel uniquement
 *  - "Client OK pour prestation" (confirm) : validation client tracée (horodatage + user)
 *
 * `onEcheancesChange(ids)` notifie le parent de la liste des rapport_id en alerte
 * afin que le dashboard puisse afficher un badge "🔔 Échéance proche" sur les
 * lignes concernées.
 */
export default function EcheancesBanner({ onEcheancesChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // `${type}-${id}`
  const [windowDays, setWindowDays] = useState(30);

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/echeances/cerfa?days=${windowDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setItems([]);
        return;
      }
      const data = await res.json();
      const list = data.items || [];
      setItems(list);
      if (onEcheancesChange) {
        onEcheancesChange(list.map((it) => ({ id: it.rapport_id, type: it.rapport_type })));
      }
    } finally {
      setLoading(false);
    }
  }, [onEcheancesChange, windowDays]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDismiss = async (item) => {
    if (!window.confirm(`Masquer l'alerte pour "${item.nom_entreprise}" ? Le rapport reste à traiter en BDD, il n'apparaîtra plus dans cette bannière.`)) return;
    const key = `${item.rapport_type}-${item.rapport_id}`;
    setActionLoading(key);
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${API_URL}/api/echeances/${item.rapport_type}/${item.rapport_id}/dismiss`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchItems();
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirm = async (item) => {
    const note = window.prompt(
      `Confirmer l'accord client pour "${item.nom_entreprise}" ?\n\nLa confirmation sera tracée (date + utilisateur). Vous pouvez ajouter une note (optionnelle) :`,
      '',
    );
    if (note === null) return; // annulation
    const key = `${item.rapport_type}-${item.rapport_id}`;
    setActionLoading(key);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/echeances/${item.rapport_type}/${item.rapport_id}/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ note: note || null }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        window.alert(`Erreur : ${err.detail || res.statusText}`);
      } else {
        await fetchItems();
      }
    } finally {
      setActionLoading(null);
    }
  };

  const rapportLink = (item) => {
    if (item.rapport_type === 'ticfe') return `/rapport/ticfe/${item.rapport_id}`;
    if (item.rapport_type === 'ticgn') return `/rapport/ticgn/${item.rapport_id}`;
    return '#';
  };

  if (loading) return null;

  // Si aucune alerte à la fenêtre par défaut, on n'affiche rien — sauf si
  // l'utilisateur a déjà étendu la fenêtre (intention d'anticiper).
  if (items.length === 0 && windowDays === 30) return null;

  const totalMontant = items.reduce((acc, it) => acc + (it.total_montant || 0), 0);

  return (
    <div
      className="mb-6 border-2 border-amber-400 rounded-lg bg-amber-50 overflow-hidden"
      data-testid="echeances-banner"
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-3 bg-amber-100 hover:bg-amber-200 transition"
        data-testid="echeances-banner-toggle"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700" />
          <div className="text-left">
            <div className="font-semibold text-amber-900">
              {items.length === 0
                ? `Aucune échéance CERFA dans les ${windowDays} prochains jours`
                : `${items.length} rapport${items.length > 1 ? 's' : ''} avec échéance CERFA proche`}
            </div>
            <div className="text-xs text-amber-800">
              {items.length > 0 && <>Total à risque : {formatEuro(totalMontant)} · </>}
              Fenêtre : {windowDays} jour{windowDays > 1 ? 's' : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={windowDays}
            onChange={(e) => { e.stopPropagation(); setWindowDays(parseInt(e.target.value, 10)); }}
            onClick={(e) => e.stopPropagation()}
            className="text-xs border border-amber-300 rounded px-2 py-1 bg-white text-amber-900"
            data-testid="echeances-window-select"
          >
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
            <option value={90}>90 jours</option>
            <option value={180}>180 jours</option>
            <option value={365}>1 an</option>
          </select>
          {expanded ? <ChevronUp className="w-4 h-4 text-amber-700" /> : <ChevronDown className="w-4 h-4 text-amber-700" />}
        </div>
      </button>

      {expanded && (
        <div className="divide-y divide-amber-200">
          {items.map((item) => {
            const key = `${item.rapport_type}-${item.rapport_id}`;
            const isLoading = actionLoading === key;
            return (
              <div
                key={key}
                className={`px-4 py-3 flex items-center justify-between gap-4 border-l-4 ${urgencyClasses(item.jours_restants)}`}
                data-testid={`echeance-row-${item.rapport_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-white border">
                      {item.rapport_type}
                    </span>
                    <Link
                      to={rapportLink(item)}
                      className="font-semibold hover:underline truncate"
                      data-testid={`echeance-link-${item.rapport_id}`}
                    >
                      {item.nom_entreprise}
                      <ExternalLink className="w-3 h-3 inline ml-1 opacity-70" />
                    </Link>
                    <span className="text-xs opacity-70">SIREN {item.siren || '—'}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-bold">{formatEuro(item.total_montant)}</span> à récupérer ·
                    <span className="ml-1">
                      {item.alerts.map((a) => `${a.annee} (J-${a.jours_restants})`).join(' · ')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleConfirm(item)}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    data-testid={`echeance-confirm-${item.rapport_id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Client OK
                  </button>
                  <button
                    onClick={() => handleDismiss(item)}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold bg-white border text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    data-testid={`echeance-dismiss-${item.rapport_id}`}
                  >
                    <BellOff className="w-3.5 h-3.5" />
                    Retirer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
